import React, { useState, useRef, useEffect, useCallback } from 'react';
import { MapPin, X } from 'lucide-react';

// Suburb-only sibling of AddressAutocomplete - same Mapbox source, but
// restricted to place/locality/postcode results instead of street addresses,
// so typing a suburb name (not a full address) actually returns matches.
const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN || '';

const parseMapboxFeature = (feature) => {
  const context = Array.isArray(feature?.context) ? feature.context : [];
  const byPrefix = (prefix) => context.find((c) => typeof c?.id === 'string' && c.id.startsWith(prefix));

  const locality = byPrefix('locality');
  const place = byPrefix('place');
  const region = byPrefix('region');
  const postcodeCtx = byPrefix('postcode');

  let state = '';
  const shortCode = region?.short_code || '';
  if (shortCode.includes('-')) {
    state = shortCode.split('-')[1].toUpperCase(); // "AU-VIC" -> "VIC"
  } else if (region?.text) {
    state = region.text;
  }

  return {
    suburb: feature?.text || locality?.text || place?.text || '',
    state,
    postcode: postcodeCtx?.text || '',
  };
};

// Props: value/onChange (the suburb input), onSelect({suburb, state, postcode})
// fired when a suggestion is picked.
const SuburbAutocomplete = ({ value, onChange, onSelect, placeholder = 'e.g. Fawkner', className = '' }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [searching, setSearching] = useState(false);
  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);
  const debounceRef = useRef(null);

  const searchSuburbs = useCallback(async (query) => {
    if (!query || query.length < 2 || !MAPBOX_TOKEN) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    setSearching(true);
    try {
      const url =
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json` +
        `?access_token=${MAPBOX_TOKEN}&country=au&autocomplete=true&limit=8&types=place,locality,postcode`;
      const response = await fetch(url);
      const data = response.ok ? await response.json() : null;
      const features = Array.isArray(data?.features) ? data.features : [];
      const mapped = features.map((feature, index) => {
        const parsed = parseMapboxFeature(feature);
        const description = String(feature?.place_name || '').replace(/,\s*Australia$/i, '');
        return { description, place_id: feature?.id || `mb-${index}`, raw: parsed };
      });
      setSuggestions(mapped);
      setShowSuggestions(mapped.length > 0);
    } catch (err) {
      console.error('Suburb search error:', err);
      setSuggestions([]);
      setShowSuggestions(false);
    } finally {
      setSearching(false);
    }
  }, []);

  const handleInputChange = (e) => {
    onChange(e);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const inputValue = e.target.value;
    debounceRef.current = setTimeout(() => searchSuburbs(inputValue), 300);
  };

  const handleSuggestionClick = (suggestion) => {
    onSelect?.(suggestion.raw);
    setShowSuggestions(false);
    setSelectedIndex(-1);
  };

  const handleKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) return;
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex((prev) => (prev < suggestions.length - 1 ? prev + 1 : prev));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
          handleSuggestionClick(suggestions[selectedIndex]);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        setSelectedIndex(-1);
        break;
      default:
        break;
    }
  };

  const handleClear = () => {
    onChange({ target: { value: '' } });
    setSuggestions([]);
    setShowSuggestions(false);
    inputRef.current?.focus();
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        suggestionsRef.current && !suggestionsRef.current.contains(event.target) &&
        inputRef.current && !inputRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative">
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
          placeholder={placeholder}
          className={className}
          autoComplete="off"
        />
        {value && !searching && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
        {searching && (
          <div className="absolute right-2 top-1/2 -translate-y-1/2">
            <div className="w-3.5 h-3.5 border-2 border-[#55C5D0] border-t-transparent rounded-full animate-spin" />
          </div>
        )}
      </div>

      {showSuggestions && suggestions.length > 0 && (
        <div
          ref={suggestionsRef}
          className="absolute z-50 w-full mt-1.5 bg-white rounded-lg border border-gray-200 shadow-xl max-h-60 overflow-y-auto"
        >
          {suggestions.map((suggestion, index) => (
            <button
              key={suggestion.place_id || index}
              type="button"
              onClick={() => handleSuggestionClick(suggestion)}
              className={`w-full text-left px-3 py-2 hover:bg-[#F0FDFE] transition-colors border-b border-gray-100 last:border-b-0 flex items-center gap-2.5 ${index === selectedIndex ? 'bg-[#F0FDFE]' : ''
                }`}
            >
              <MapPin className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
              <span className="text-[13px] text-gray-800 truncate">{suggestion.description}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default SuburbAutocomplete;
